import { FormEvent, useCallback, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { Mountain, Compass, Lock, Mail } from 'lucide-react'

import { GoogleSignInButton } from '../components/auth/GoogleSignInButton'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { useAuth } from '../hooks/useAuth'

const EMAIL_STORAGE_KEY = 'hikestrong-dashboard-email'

const getAuthErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<{ message?: unknown }>(error)) {
    return fallback
  }

  const message = error.response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

export const LoginPage = () => {
  const navigate = useNavigate()
  const { login, loginWithGoogle, loading } = useAuth()
  const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim()

  // Read directly from localStorage to avoid race condition with useAuth's useEffect
  const storedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY)
  const [email, setEmail] = useState(storedEmail ?? '')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(!!storedEmail)
  const [error, setError] = useState<string | null>(null)
  const [googleLink, setGoogleLink] = useState<{ idToken: string; email: string } | null>(null)
  const [linkPassword, setLinkPassword] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)

  const clearGoogleLink = () => {
    setGoogleLink(null)
    setLinkPassword('')
    setLinkError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    try {
      await login(email, password, rememberMe)
      navigate('/plans', { replace: true })
    } catch {
      setError('Unable to log in with the provided credentials.')
    }
  }

  const handleGoogleCredential = useCallback(async (credential: string) => {
    setError(null)
    try {
      await loginWithGoogle(credential, true)
      navigate('/plans', { replace: true })
    } catch (err) {
      if (axios.isAxiosError<{ code?: string; email?: string }>(err) &&
          err.response?.status === 409 &&
          err.response.data.code === 'google_link_confirmation_required' &&
          err.response.data.email) {
        setGoogleLink({ idToken: credential, email: err.response.data.email })
        setLinkPassword('')
        setLinkError(null)
        return
      }
      setError(getAuthErrorMessage(err, 'Unable to sign in with Google. Please try again.'))
    }
  }, [loginWithGoogle, navigate])

  const handleConfirmGoogleLink = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!googleLink) return
    setLinkError(null)
    const passwordToConfirm = linkPassword
    setLinkPassword('')
    try {
      await loginWithGoogle(googleLink.idToken, true, passwordToConfirm)
      clearGoogleLink()
      navigate('/plans', { replace: true })
    } catch (err) {
      setLinkError(getAuthErrorMessage(err, 'Unable to connect Google. Please try again.'))
    }
  }

  return (
    <div className="flex min-h-screen">
      <Dialog open={googleLink !== null} onOpenChange={(open) => { if (!open && !loading) clearGoogleLink() }}>
        <DialogContent className="top-1/2 max-w-md -translate-y-1/2 p-6">
          <DialogTitle>Connect your Google account</DialogTitle>
          <DialogDescription className="mt-3">
            Confirm the dashboard password for {googleLink?.email} once. Next time, you can sign in with Google directly.
          </DialogDescription>
          <form className="mt-5 space-y-4" onSubmit={handleConfirmGoogleLink}>
            {linkError && <p className="text-sm text-red-700" role="alert">{linkError}</p>}
            <label htmlFor="google-link-password" className="block text-sm font-semibold">Dashboard password</label>
            <Input
              id="google-link-password"
              type="password"
              autoComplete="current-password"
              required
              value={linkPassword}
              onChange={(event) => setLinkPassword(event.target.value)}
              disabled={loading}
            />
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" disabled={loading} onClick={clearGoogleLink}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Connecting…' : 'Connect and sign in'}</Button>
            </div>
            <Link to="/forgot-password" className="block text-sm font-semibold text-primary hover:underline">Forgot your dashboard password?</Link>
          </form>
        </DialogContent>
      </Dialog>
      {/* Left Side - Branding & Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-primary via-primary-hover to-primary">
        {/* Topographic overlay */}
        <div className="absolute inset-0 topo-lines opacity-20"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-primary-foreground">
          {/* Logo & Brand */}
          <div>
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-foreground/20 backdrop-blur-sm shadow-2xl border-2 border-primary-foreground/30">
                <Mountain className="h-9 w-9" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-accent">HikeStrong</p>
                <h2 className="text-2xl font-bold tracking-tight">Base Camp</h2>
              </div>
            </div>
            <div className="max-w-md space-y-4">
              <h1 className="text-4xl font-bold leading-tight">
                Trail Operations Command Center
              </h1>
              <p className="text-lg leading-relaxed text-primary-foreground/80">
                Manage tour operations, guide adventurers, and build unforgettable hiking experiences
              </p>
            </div>
          </div>

          {/* Trail Stats / Features */}
          <div className="grid grid-cols-3 gap-6 max-w-lg">
            {[
              { label: 'Tour Operators', value: 'Unlimited' },
              { label: 'Routes', value: 'Global' },
              { label: 'Uptime', value: '99.9%' }
            ].map((stat, i) => (
              <div key={i} className="rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 backdrop-blur-sm p-4">
                <div className="font-mono text-2xl font-bold">{stat.value}</div>
                <div className="mt-1 text-xs text-primary-foreground/70">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Compass decoration */}
          <div className="absolute bottom-12 right-12 opacity-10">
            <Compass className="h-64 w-64" strokeWidth={0.5} />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-background topo-pattern p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg">
              <Mountain className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent">HikeStrong</p>
              <h2 className="text-xl font-bold tracking-tight text-foreground">Base Camp</h2>
            </div>
          </div>

          {/* Form Card */}
          <div className="elevation-card rounded-2xl p-8">
            <div className="mb-8">
              <div className="mb-3 flex items-center gap-2">
                <div className="h-1 w-8 rounded-full bg-accent"></div>
                <span className="font-mono text-xs font-bold uppercase tracking-wider text-accent">
                  Operator Access
                </span>
              </div>
              <h1 className="text-3xl font-bold text-foreground">Welcome Back</h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Sign in to access your trail operations dashboard
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3" role="alert">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            {googleClientId && (
              <div className="mb-5 space-y-3">
                <GoogleSignInButton
                  clientId={googleClientId}
                  disabled={loading}
                  onCredential={handleGoogleCredential}
                />
                <p className="text-center text-xs leading-relaxed text-muted-foreground">
                  Uses the Google email invited to Base Camp and remembers this browser for 30 days.
                </p>
                <div className="flex items-center gap-3" aria-hidden="true">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    or use a password
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="login-email" className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email Address
                </label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="guide@hikestrong.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="login-password" className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Password
                </label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="h-12"
                  required
                />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border border-border text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                  />
                  Remember me for 30 days
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"></div>
                    Authenticating...
                  </span>
                ) : (
                  'Sign In to Base Camp'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                <Link to="/forgot-password" className="font-semibold text-primary hover:underline">
                  Forgot your password?
                </Link>
              </p>
            </div>
          </div>

          {/* Footer Help */}
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Dashboard access is invite-only. Ask an admin to create an account for your Google email.
          </p>
        </div>
      </div>
    </div>
  )
}
